import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { Link, useHistory } from 'react-router-dom';

import { Box, Container } from '@mui/material';

import { operations } from '@luxuryescapes/contract-svc-promo';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useCurrentTenant from '~/hooks/useCurrentTenant';
import useQuery from '~/hooks/useQuery';

import { expirePromoDisplayConfig, getPromoDisplayConfigs } from '~/services/PromoService';

import PromoDisplayCheckForm from './PromoDisplayCheckForm';
import PromoDisplayConfigTab from './PromoDisplayConfigTab';

type PromoDisplayConfig = 'active' | 'expired' | undefined;
type PromoRegionType = operations['getPromoDisplayConfigs']['parameters']['query']['region'];
type PromoDisplayConfigType =
  operations['getPromoDisplayConfigs']['responses']['200']['content']['application/json']['result']['promoDisplayConfigs'];

function PromoDisplayConfigContainer() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<PromoDisplayConfigType>([] as PromoDisplayConfigType);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const [urlPath, setUrlPath] = useState<string>(query.get('url_path') || undefined);
  const [promoCodeName, setPromoCodeName] = useState<string>(query.get('code_name') || undefined);
  const [region, setRegion] = useState<PromoRegionType>(query.get('region') as PromoRegionType);
  const [source, setSource] = useState<string>(query.get('source') || 'ALL');
  const [medium, setMedium] = useState<string>(query.get('medium') || 'ALL');
  const [campaign, setCampaign] = useState<string>(query.get('campaign') || 'ALL');
  const [status, setStatus] = useState<PromoDisplayConfig | 'all'>(
    (query.get('status') as PromoDisplayConfig | 'active') || 'active',
  );
  const [totalCount, setTotalCount] = useState(0);
  const { tenant } = useCurrentTenant();
  const history = useHistory();
  const [editableConfig, setEditableConfig] = useState<
    operations['updatePromoDisplayConfig']['requestBody']['content']['application/json'] | null
  >(null);

  // Check if we should show the check form
  const promoDisplayConfigId = query.get('promoDisplayConfigId');
  const showCheckForm = !!promoDisplayConfigId;

  // Check for editingConfigId in URL
  const editingConfigId = query.get('editingConfigId');

  const updateQueryParams = useCallback(
    (additionalParams: Record<string, string | undefined> = {}) => {
      const params: Record<string, string> = {
        page_num: pageNum.toString(),
        page_size: pageSize.toString(),
        status: status,
        promo_code_name: promoCodeName,
        url_path: urlPath,
        source,
        medium,
        campaign,
        region,
        ...Object.fromEntries(Object.entries(additionalParams).filter(([_, value]) => value !== undefined)),
      };
      const searchParams = qs.stringify(params);
      history.push({
        search: `?${searchParams}`,
      });
    },
    [history, pageNum, pageSize, status, promoCodeName, urlPath, source, medium, campaign, region],
  );

  // START - New Markup Modal
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setEditableConfig(null);
    setOpen(true);
    // Remove editingConfigId from URL when opening new config
    updateQueryParams({ editingConfigId: undefined });
  };

  const handleResultsOpen = (promoDisplayConfigId?: string) => {
    if (promoDisplayConfigId) {
      history.push(`/marketing/offer-metrics?tab=3&promoDisplayConfigId=${promoDisplayConfigId}`);
    } else {
      history.push(`/marketing/offer-metrics?tab=3`);
    }
  };

  const handleEditPromoDisplayConfig = (config) => {
    setEditableConfig(config);
    setOpen(true);
    // Update URL with editingConfigId
    updateQueryParams({ editingConfigId: config.promo_display_config_id });
  };

  const handleClonePromoDisplayConfig = (config) => {
    // Create a clone of the config without the ID and with a modified code name
    const clonedConfig = {
      ...config,
      promo_display_config_id: undefined,
      code_name: config.code_name,
    };
    setEditableConfig(clonedConfig);
    setOpen(true);
    // Remove editingConfigId from URL when cloning (creating new)
    updateQueryParams({ editingConfigId: undefined });
  };

  // END - New Markup Modal

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const setQueryParams = useCallback(() => {
    // Preserve editingConfigId if it exists in URL
    const currentEditingConfigId = query.get('editingConfigId');
    updateQueryParams(currentEditingConfigId ? { editingConfigId: currentEditingConfigId } : {});
  }, [updateQueryParams, query]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        result: { promoDisplayConfigs, total },
      } = await getPromoDisplayConfigs({
        page: pageNum,
        pageSize: pageSize,
        status: status === 'all' ? undefined : status,
        code_name: promoCodeName,
        url_path: urlPath,
        source,
        medium,
        campaign,
        brand: tenant.brand === 'newwhitelabel' || tenant.brand === 'yidu' ? 'luxuryescapes' : tenant.brand,
        region: region ?? 'AU',
      });
      setData(promoDisplayConfigs ?? []);
      setIsLoading(false);
      setTotalCount(Number(total));
      setQueryParams();

      // After loading data, check if we need to open edit modal for editingConfigId
      // Only open if we're not already editing this config to avoid re-triggering on data refresh
      if (editingConfigId && promoDisplayConfigs) {
        const configToEdit = promoDisplayConfigs.find((config) => config.promo_display_config_id === editingConfigId);
        if (configToEdit) {
          // Only set if we're not already editing this config
          if (editableConfig?.promo_display_config_id !== editingConfigId) {
            setEditableConfig(configToEdit);
            setOpen(true);
          }
        } else {
          // Config not found in current page, clear the URL param
          updateQueryParams({ editingConfigId: undefined });
        }
      }
    } catch (error) {
      enqueueSnackbar('Promo Display Config fetch failed', { variant: 'error' });
      return;
    }
  }, [
    pageNum,
    pageSize,
    status,
    promoCodeName,
    urlPath,
    source,
    medium,
    campaign,
    tenant.brand,
    region,
    setQueryParams,
    enqueueSnackbar,
    editingConfigId,
    updateQueryParams,
  ]);

  const dataRows = data.map((row, index) => ({
    id: index,
    ...row,
  }));

  // Update the handleExpirePromoConfig to use fetchData
  const handleExpirePromoConfigWithFetch = useCallback(
    async ({ id }: { id: string }) => {
      try {
        await expirePromoDisplayConfig(id);
        enqueueSnackbar('Expired the promo display config successfully', { variant: 'success' });
        fetchData();
      } catch (error) {
        enqueueSnackbar(`Failed to expired the promo display config: ${error}`, { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchData],
  );
  const handlePromoDisplayNavigate = useCallback(
    (config: any) => {
      history.push(`/marketing/offer-metrics?tab=3&promoDisplayConfigId=${config.promo_display_config_id}`);
    },
    [history],
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 500);
    return () => clearTimeout(timer);
  }, [pageNum, pageSize, status, promoCodeName, urlPath, source, medium, campaign, region, fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      query.set('page_num', page.toString());
      query.set('page_size', pageSize.toString());
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
      fetchData();
    },
    [fetchData, query],
  );

  return (
    <Container maxWidth="xl" sx={{ width: '90%' }}>
      <Helmet>
        <title>Promo Display Config</title>
      </Helmet>

      {showCheckForm ? (
        <Box>
          <PageSubheader title="Check Promo Display Config">
            <Link to="/marketing/offer-metrics">Offer Metrics</Link> for offers with specific metric.
          </PageSubheader>
          <Box mt={4}>
            <PromoDisplayCheckForm />
          </Box>
        </Box>
      ) : (
        <Box>
          <PageSubheader title={`Search Promo Display Config [${data?.length} of ${totalCount}]`} />
          <PromoDisplayConfigTab
            promoCodeName={promoCodeName}
            setPromoCodeName={setPromoCodeName}
            urlPath={urlPath}
            setUrlPath={setUrlPath}
            region={region}
            setRegion={setRegion}
            source={source}
            setSource={setSource}
            medium={medium}
            setMedium={setMedium}
            campaign={campaign}
            setCampaign={setCampaign}
            status={status}
            setStatus={setStatus}
            data={data}
            isLoading={isLoading}
            totalCount={totalCount}
            pageNum={pageNum}
            pageSize={pageSize}
            onPageChange={onPageChange}
            handleStatusChange={handleStatusChange}
            handleOpen={handleOpen}
            handleResultsOpen={handleResultsOpen}
            handleExpirePromoConfigWithFetch={handleExpirePromoConfigWithFetch}
            handleEditPromoDisplayConfig={handleEditPromoDisplayConfig}
            handleClonePromoDisplayConfig={handleClonePromoDisplayConfig}
            handlePromoDisplayNavigate={handlePromoDisplayNavigate}
            open={open}
            setOpen={(newOpen) => {
              setOpen(newOpen);
              // Clear editingConfigId from URL when closing modal
              if (!newOpen) {
                updateQueryParams({ editingConfigId: undefined });
              }
            }}
            editableConfig={editableConfig}
            fetchData={fetchData}
          />
        </Box>
      )}
    </Container>
  );
}

export default PromoDisplayConfigContainer;
